import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { eq } from 'ember-truth-helpers';
import BvText from 'volta/components/bv-text';
import classNames from 'volta/helpers/class-names';
import stopPropagation from 'volta/helpers/stop-propagation';
import { splitToLines, tryParseRouteInfo, tryParseURL } from 'volta/utils/text-utils';

import type RouteInfo from '@ember/routing/route-info';
import type RouterService from '@ember/routing/router-service';

interface BvRichTextSignature {
  Args: {
    text?: string;
    blockClassName?: string;
  };
  Element: HTMLParagraphElement;
}

type TTextPartType = 'text' | 'internal-link' | 'external-link';
type TPart = { type: TTextPartType; value?: string; routeInfo?: RouteInfo; url?: URL };
type TParts = TPart[][];

export default class BvRichTextComponent extends Component<BvRichTextSignature> {
  @service router!: RouterService;

  @cached
  get textParts(): TParts {
    return this.args.text
      ? splitToLines(this.args.text).map((p) => {
          const parts: TPart[] = [];
          let text: string[] = [];
          const words = p.trim().split(' ');
          words.forEach((w, i) => {
            const word = w.trim();

            // We first check if the word is a URL,
            // and if it is, we check if it's an internal link

            const url = tryParseURL(word);
            const routeInfo =
              url?.origin === document.location.origin
                ? tryParseRouteInfo(url, this.router)
                : undefined;
            const type = url ? (routeInfo ? 'internal-link' : 'external-link') : 'text';

            // We accumulate text parts until we find a link
            // or we reach the end of the line

            if (type === 'text') {
              text.push(word);
            } else {
              if (text.length > 0) {
                parts.push({
                  type: 'text',
                  value: text.join(' ')
                });
                text = [];
              }

              parts.push({
                type,
                routeInfo,
                value: word,
                url
              });
            }

            if (i === words.length - 1 && text.length > 0) {
              parts.push({
                type: 'text',
                value: text.join(' ')
              });
            }
          });
          return parts;
        })
      : [];
  }

  @action
  handleInternalLinkClick(url: URL, event: MouseEvent) {
    event.preventDefault();
    this.router.transitionTo(`${url.pathname}${url.search}${url.hash}`);
  }

  <template>
    {{#each this.textParts as |paragraph|}}
      <p class={{classNames 'bv-rich-text__paragraph' @blockClassName}} ...attributes>
        {{#each paragraph as |word|}}
          {{#if (eq word.type 'internal-link')}}
            {{#if word.url}}
              <a
                href={{word.url.href}}
                class={{concat 'bv-rich-text__' word.type}}
                title={{word.url.href}}
                {{on 'click' (fn this.handleInternalLinkClick word.url)}}
              >
                {{word.url.href}}
              </a>
            {{/if}}

          {{else if (eq word.type 'external-link')}}
            <a
              class={{concat 'bv-rich-text__' word.type}}
              href={{word.url.href}}
              target='_blank'
              rel='noopener noreferrer'
              title={{word.url.href}}
              {{on 'click' (stopPropagation)}}
            >
              {{word.url.href}}
            </a>
          {{else}}
            <BvText class={{concat 'bv-rich-text__' word.type}} @text={{word.value}} />
          {{/if}}
        {{/each}}
      </p>
    {{/each}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvRichText: typeof BvRichTextComponent;
    'bv-rich-text': typeof BvRichTextComponent;
  }
}
